"use client";

import { useState, useCallback, useRef } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  Receipt,
  Info,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  Calculator,
  ChevronDown,
  ChevronUp,
  Table,
} from "lucide-react";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { RelatedLinks } from "@/components/seo/RelatedLinks";
import {
  StructuredData,
  generateHowToSchema,
} from "@/components/seo/StructuredData";
import {
  type GelirVergisiSonuc,
  BRUT_ASGARI_UCRET,
  VERGI_DILIMLERI_2026,
  hesaplaGelirVergisi,
  formatTL,
  formatOran,
  YILI,
} from "./gelir-vergisi-engine";

// ─── HowTo Schema ─────────────────────────────────────────────
const howToSchema = generateHowToSchema({
  title: "Gelir Vergisi Nasıl Hesaplanır?",
  description:
    "Brüt maaşınızı girerek 2026 kümülatif gelir vergisi dilimlerine göre aylık ve yıllık vergilerinizi hesaplayın.",
  totalTime: "PT2M",
  steps: [
    {
      name: "Brüt Maaşınızı Girin",
      text: "Aylık brüt maaşınızı TL olarak girin. Asgari ücret butonu ile otomatik doldurabilirsiniz.",
    },
    {
      name: "Hesapla Butonuna Tıklayın",
      text: "Sistem, 12 aylık kümülatif gelir vergisi matrahını ve her ay için ayrı ayrı vergi hesaplayacaktır.",
    },
    {
      name: "Aylık Detayları İnceleyin",
      text: "Her ay için brüt, kesintiler, gelir vergisi, net maaş ve içinde bulunduğunuz vergi dilimini görüntüleyin.",
    },
  ],
});

// ─── SSS Bileşeni ─────────────────────────────────────────────
function SSSSoru({ soru, cevap }: { soru: string; cevap: string }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAcik((p) => !p)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={acik}
      >
        <span className="font-semibold text-gray-900 pr-4">{soru}</span>
        {acik ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>
      {acik && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 leading-relaxed">{cevap}</p>
        </div>
      )}
    </div>
  );
}

// ─── Sonuç Satırı ─────────────────────────────────────────────
function SonucSatir({
  label,
  value,
  variant = "normal",
  bold = false,
}: {
  label: string;
  value: string;
  variant?: "normal" | "kesinti" | "eklenti" | "toplam";
  bold?: boolean;
}) {
  const renkler = {
    normal: "text-gray-900",
    kesinti: "text-red-600",
    eklenti: "text-blue-600",
    toplam: "text-emerald-600",
  };
  return (
    <div
      className={`flex justify-between items-center py-1.5 ${bold ? "pt-3 border-t border-gray-200" : ""}`}
    >
      <span
        className={`text-sm ${bold ? "font-bold text-gray-900" : "text-gray-600"}`}
      >
        {label}
      </span>
      <span
        className={`text-sm font-medium tabular-nums ${bold ? "text-base font-bold" : ""} ${renkler[variant]}`}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Ana Bileşen ──────────────────────────────────────────────
export default function GelirVergisiHesaplayiciClient() {
  const [brutMaas, setBrutMaas] = useState("");
  const [sonuc, setSonuc] = useState<GelirVergisiSonuc | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [hesaplandi, setHesaplandi] = useState(false);
  const [tabloAcik, setTabloAcik] = useState(false);

  const maasInputRef = useRef<HTMLInputElement>(null);

  const hesapla = useCallback(() => {
    setHata(null);
    const maas = parseFloat(brutMaas);

    if (!brutMaas || isNaN(maas) || maas <= 0) {
      setHata("Lütfen geçerli bir brüt maaş giriniz.");
      setSonuc(null);
      setHesaplandi(false);
      return;
    }

    const hesapSonuc = hesaplaGelirVergisi({
      hesaplamaTipi: "brut_ucret",
      brutAylikUcret: maas,
    });

    setSonuc(hesapSonuc);
    setHesaplandi(true);
    setHata(null);
  }, [brutMaas]);

  const asgariUcretDoldur = useCallback(() => {
    setBrutMaas(BRUT_ASGARI_UCRET.toString());
  }, []);

  const sifirla = useCallback(() => {
    setBrutMaas("");
    setSonuc(null);
    setHata(null);
    setHesaplandi(false);
    setTabloAcik(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        hesapla();
      }
    },
    [hesapla]
  );

  return (
    <>
      <StructuredData data={[howToSchema]} />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* ─── Hero ─────────────────────────────────────────── */}
        <section className="bg-gradient-to-br from-violet-600 to-purple-700 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Breadcrumbs
              items={[
                { label: "Araçlar", href: "/araclar" },
                {
                  label: "Gelir Vergisi Hesaplayıcı",
                  href: "/araclar/gelir-vergisi-hesaplayici",
                },
              ]}
              className="mb-8 text-white/70"
            />

            <Link
              href="/araclar"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tüm Araçlar
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <Receipt className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">
                  Gelir Vergisi Hesaplayıcı
                </h1>
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                  {YILI} Güncel Dilimler
                </span>
              </div>
            </div>
            <p className="text-lg text-white/90 max-w-2xl">
              {YILI} kümülatif gelir vergisi dilimlerine göre aylık ve yıllık
              verginizi hesaplayın. 12 aylık detaylı tablo ile hangi ayda hangi
              dilime geçtiğinizi görün.
            </p>
          </div>
        </section>

        {/* ─── Hesaplama Formu ──────────────────────────────── */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              {/* Vergi Dilimleri Özet Tablosu */}
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">
                  {YILI} Gelir Vergisi Dilimleri (Ücret Gelirleri)
                </h2>
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-violet-50">
                        <th className="text-left py-2.5 px-3 font-semibold text-gray-900">
                          Matrah Dilimi
                        </th>
                        <th className="text-right py-2.5 px-3 font-semibold text-gray-900">
                          Oran
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {VERGI_DILIMLERI_2026.map((dilim, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="py-2.5 px-3 text-gray-700">
                            {dilim.label}
                          </td>
                          <td className="py-2.5 px-3 text-right font-semibold text-violet-600">
                            {formatOran(dilim.oran)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Brüt Maaş Girişi */}
              <div className="mb-8">
                <label
                  htmlFor="brutMaas"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  Aylık Brüt Maaş
                </label>
                <div className="relative max-w-md">
                  <input
                    ref={maasInputRef}
                    id="brutMaas"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="100"
                    value={brutMaas}
                    onChange={(e) => setBrutMaas(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="0,00"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-gray-900 placeholder-gray-400 transition-shadow"
                    aria-label="Aylık brüt maaş"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                    ₺
                  </span>
                </div>
                <button
                  type="button"
                  onClick={asgariUcretDoldur}
                  className="mt-2 text-xs text-violet-600 hover:text-violet-700 font-medium transition-colors"
                >
                  {YILI} Asgari Ücret Uygula ({formatTL(BRUT_ASGARI_UCRET)})
                </button>
              </div>

              {/* Hata */}
              {hata && (
                <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{hata}</p>
                </div>
              )}

              {/* Butonlar */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={hesapla}
                  className="flex-1 max-w-md bg-violet-600 text-white py-4 rounded-xl font-semibold hover:bg-violet-700 active:bg-violet-800 transition-colors shadow-sm"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Hesapla
                  </span>
                </button>
                {hesaplandi && (
                  <button
                    type="button"
                    onClick={sifirla}
                    className="px-6 py-4 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Sıfırla
                  </button>
                )}
              </div>
            </div>

            {/* ─── Sonuç Alanı ───────────────────────────────── */}
            {sonuc && (
              <div className="border-t border-gray-200 bg-gray-50 p-6 sm:p-8">
                {/* Özet Kartları */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                  <div className="bg-violet-600 text-white rounded-xl p-4">
                    <span className="text-xs text-violet-200 font-medium block">
                      Yıllık Gelir Vergisi
                    </span>
                    <span className="text-lg sm:text-xl font-bold mt-1 block tabular-nums">
                      {formatTL(sonuc.yillikGelirVergisi)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 font-medium block">
                      Ortalama Efektif Oran
                    </span>
                    <span className="text-lg sm:text-xl font-bold text-violet-600 mt-1 block tabular-nums">
                      {formatOran(sonuc.ortalamaEfektifOran)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 font-medium block">
                      Yıllık Net Ücret
                    </span>
                    <span className="text-lg sm:text-xl font-bold text-emerald-600 mt-1 block tabular-nums">
                      {formatTL(sonuc.yillikNetUcret)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 font-medium block">
                      Yıllık Toplam Kesinti
                    </span>
                    <span className="text-lg sm:text-xl font-bold text-red-600 mt-1 block tabular-nums">
                      {formatTL(sonuc.yillikToplamKesinti)}
                    </span>
                  </div>
                </div>

                {/* Yıllık Detay */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Yıllık Kesinti Dökümü */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="w-5 h-5 text-violet-600" />
                      <h3 className="font-bold text-gray-900">
                        Yıllık Kesinti Dökümü
                      </h3>
                    </div>
                    <div className="space-y-0.5">
                      <SonucSatir
                        label="Yıllık brüt ücret"
                        value={formatTL(sonuc.yillikBrutUcret)}
                      />
                      <SonucSatir
                        label="SGK işçi payı (%14)"
                        value={`-${formatTL(sonuc.yillikSgkKesinti)}`}
                        variant="kesinti"
                      />
                      <SonucSatir
                        label="İşsizlik sigortası (%1)"
                        value={`-${formatTL(sonuc.yillikIssizlikKesinti)}`}
                        variant="kesinti"
                      />
                      <SonucSatir
                        label="Gelir vergisi (kümülatif)"
                        value={`-${formatTL(sonuc.yillikGelirVergisi)}`}
                        variant="kesinti"
                      />
                      <SonucSatir
                        label="Damga vergisi (%0,759)"
                        value={`-${formatTL(sonuc.yillikDamgaVergisi)}`}
                        variant="kesinti"
                      />
                      <SonucSatir
                        label="Yıllık Net Ücret"
                        value={formatTL(sonuc.yillikNetUcret)}
                        variant="toplam"
                        bold
                      />
                    </div>
                  </div>

                  {/* Dilim Dağılımı */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <BarChart3 className="w-5 h-5 text-violet-600" />
                      <h3 className="font-bold text-gray-900">
                        Vergi Dilimi Dağılımı
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {sonuc.dilimDagilimi.map((d, i) => {
                        const yuzde =
                          sonuc.yillikMatrah > 0
                            ? (d.matrah / sonuc.yillikMatrah) * 100
                            : 0;
                        return (
                          <div key={i}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600">
                                {formatOran(d.dilim.oran)} dilimi
                              </span>
                              <span className="font-medium text-gray-900 tabular-nums">
                                {formatTL(d.vergi)}
                              </span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div
                                className="bg-violet-500 h-2 rounded-full transition-all"
                                style={{ width: `${Math.min(100, yuzde)}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5 tabular-nums">
                              Matrah: {formatTL(d.matrah)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* 12 Aylık Detay Tablo */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setTabloAcik((p) => !p)}
                    className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Table className="w-5 h-5 text-violet-600" />
                      <span className="font-bold text-gray-900">
                        12 Aylık Detaylı Tablo
                      </span>
                    </div>
                    {tabloAcik ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {tabloAcik && (
                    <div className="overflow-x-auto border-t border-gray-200">
                      <table className="w-full text-xs sm:text-sm whitespace-nowrap">
                        <thead>
                          <tr className="bg-violet-50 border-b border-gray-200">
                            <th className="text-left py-3 px-3 font-semibold text-gray-900">
                              Ay
                            </th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-900">
                              Brüt
                            </th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-900">
                              SGK+İşsz.
                            </th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-900">
                              G.V.
                            </th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-900">
                              Damga
                            </th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-900">
                              Net
                            </th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-900">
                              Küm. Matrah
                            </th>
                            <th className="text-left py-3 px-3 font-semibold text-gray-900">
                              Dilim
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {sonuc.aylikDetaylar.map((ay) => (
                            <tr key={ay.ay} className="hover:bg-gray-50">
                              <td className="py-2.5 px-3 font-medium text-gray-900">
                                {ay.ayAdi}
                              </td>
                              <td className="py-2.5 px-3 text-right tabular-nums text-gray-700">
                                {formatTL(ay.aylikBrut)}
                              </td>
                              <td className="py-2.5 px-3 text-right tabular-nums text-red-600">
                                -{formatTL(ay.sgkKesinti + ay.issizlikKesinti)}
                              </td>
                              <td className="py-2.5 px-3 text-right tabular-nums text-red-600">
                                -{formatTL(ay.aylikGelirVergisi)}
                              </td>
                              <td className="py-2.5 px-3 text-right tabular-nums text-red-600">
                                -{formatTL(ay.damgaVergisi)}
                              </td>
                              <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-emerald-600">
                                {formatTL(ay.netUcret)}
                              </td>
                              <td className="py-2.5 px-3 text-right tabular-nums text-gray-500">
                                {formatTL(ay.kumulatifMatrah)}
                              </td>
                              <td className="py-2.5 px-3 text-gray-500 text-xs">
                                {ay.icindeBulunanDilim}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bilgilendirme */}
          <div className="mt-8 p-5 bg-amber-50 rounded-xl border border-amber-100">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Önemli Bilgiler
                </h3>
                <ul className="text-sm text-gray-600 space-y-1.5">
                  <li>
                    • Bu hesaplama {YILI} yılı ücret gelirlerine ilişkin vergi
                    dilimleri esas alınarak yapılmıştır.
                  </li>
                  <li>
                    • SGK primine esas kazanç tavanı ({formatTL(297_270)})
                    üzerindeki brüt ücretlerde SGK kesintisi tavanda sabitlenir.
                  </li>
                  <li>
                    • AGİ (Asgari Geçim İndirimi) 2022&apos;den itibaren
                    kaldırılmıştır.
                  </li>
                  <li>
                    • Kesin bordro hesaplaması için muhasebe departmanınıza
                    danışınız.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ─── İçerik / Rehber ──────────────────────────────── */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <div className="prose prose-gray max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Gelir Vergisi Hesaplama Rehberi ({YILI})
            </h2>
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Gelir Vergisi Nedir?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Gelir vergisi, gerçek kişilerin bir takvim yılı içinde elde
                  ettiği kazanç ve iratların safi tutarı üzerinden alınan bir
                  vergidir. Ücret gelirleri için gelir vergisi, kümülatif matrah
                  üzerinden artan oranlı dilim sistemi ile hesaplanır.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Kümülatif Vergi Sistemi Nasıl Çalışır?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Her ay brüt maaşınızdan SGK ve işsizlik sigortası kesildikten
                  sonra kalan tutar &quot;gelir vergisi matrahı&quot; olur. Bu
                  matrah yılbaşından itibaren biriktirilir (kümülatif).
                  Kümülatif matrah bir üst vergi diliminin sınırını aştığında, o
                  aydan itibaren fazlası üst dilim oranıyla vergilendirilir ve
                  net maaşınız düşer.
                </p>
              </div>

              {/* SSS */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Sık Sorulan Sorular
                </h3>
                <div className="space-y-3">
                  <SSSSoru
                    soru={`${YILI} gelir vergisi dilimleri nelerdir?`}
                    cevap={`${YILI} ücret gelirleri dilimleri: 0-190.000 ₺ %15, 190.001-400.000 ₺ %20, 400.001-1.500.000 ₺ %27, 1.500.001-5.300.000 ₺ %35, 5.300.001 ₺ ve üzeri %40.`}
                  />
                  <SSSSoru
                    soru="Kümülatif gelir vergisi ne demek?"
                    cevap="Kümülatif gelir vergisi, yılbaşından itibaren biriken gelir vergisi matrahının vergi dilimine göre hesaplanmasıdır. Yıl içinde maaşınız aynı kalsa bile, kümülatif matrah arttıkça üst dilime geçilir ve net maaşınız düşer."
                  />
                  <SSSSoru
                    soru="Hangi ayda vergi dilimim değişir?"
                    cevap="Bu, brüt maaşınıza bağlıdır. Hesaplayıcıdaki 12 aylık tablodan hangi ayda hangi dilime geçtiğinizi görebilirsiniz. Genel olarak yüksek maaşlarda yılın ortasına doğru dilim değişimi yaşanır."
                  />
                  <SSSSoru
                    soru="Damga vergisi nedir?"
                    cevap="Damga vergisi, brüt ücret üzerinden %0,759 oranında kesilen bir vergidir. Gelir vergisi matrahından bağımsız olarak brüt tutar üzerinden hesaplanır."
                  />
                  <SSSSoru
                    soru="AGİ hâlâ geçerli mi?"
                    cevap="Hayır. Asgari Geçim İndirimi (AGİ) 2022 yılından itibaren kaldırılmıştır. Asgari ücretlilerin maaşından gelir vergisi ve damga vergisi kesilmemektedir."
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-bold text-gray-900 mb-2">
              Sonuc Yorumu ve Aksiyon Onerisi
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Kumulatif matrah etkisini ay bazli izlemek, net ucretteki dususu
              onceden gormenizi saglar. Ucret ve yan hak planini bu tabloya gore
              guncellemek daha dogru bir yaklasimdir.
            </p>
            <div className="flex flex-wrap gap-2 text-sm">
              <Link
                href="/yazilim/ucret-ve-yan-hak-yonetimi"
                className="text-indigo-600 hover:underline"
              >
                Ucret ve yan hak yonetimi
              </Link>
              <Link
                href="/araclar/kurumlar-vergisi-hesaplayici"
                className="text-indigo-600 hover:underline"
              >
                Kurumlar vergisi hesaplayici
              </Link>
              <Link
                href="/cozumler/analitik"
                className="text-indigo-600 hover:underline"
              >
                IK analitik raporlama
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Vergi Dilimi Takip Plani
            </h2>
            <h3 className="text-base font-semibold text-gray-800 mb-2">
              Kumulatif matrah aylik izlenmelidir
            </h3>
            <p className="text-sm text-gray-600">
              Ucret ve yan hak kararlarini planlarken yil ici dilim gecisleri ve
              net ucret etkisi birlikte analiz edilmelidir.
            </p>
          </div>
        </section>

        {/* Related Links */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <RelatedLinks
            pageType="tool"
            currentSlug="gelir-vergisi-hesaplayici"
            title="İlgili Sayfalar"
            maxLinks={4}
          />
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-indigo-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">
              Bordro Yönetimini Otomatikleştirin
            </h2>
            <p className="text-indigo-100 mb-6 max-w-xl mx-auto">
              IKAI ile otomatik vergi hesaplama, kümülatif matrah takibi ve
              yasal güncelleme tek platformda.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 bg-white text-indigo-700 px-6 py-3 rounded-xl font-semibold hover:bg-indigo-50 transition-colors"
            >
              Ücretsiz Deneyin
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
